import Flex from '@Components/Bases/Flex';
import FormCheckbox from '@Components/Form/FormCheckBox';
import { Box, Divider, Typography } from '@mui/material';
import { CompleteSectionFormValues } from '@Utils/Types/FormValuesTypes';
import {
  validateEnglishDescription,
  validateThaiDescription,
} from '@Utils/validation';
import { useEffect } from 'react';
import { useWatch } from 'react-hook-form';
import { useProgressBar } from 'src/Constant/ProgressBarsContext/ProgressBar.Context';
import {
  checkBizAssetsValidEn,
  checkBizAssetsValidTh,
  checkBizIntroductionEnValid,
  checkBizIntroductionThValid,
  checkBizRemarkEnValid,
  checkBizRemarkThValid,
  checkBizValueCompletionEn,
  checkBizValueCompletionTh,
} from '../BusinessValueChain/utils';
import { useImpactsOnStakeholdersForm } from '../ImpactsOnStakeholdersFormProvider';
import { StakeholderAnalysisFormValues } from '../ImpactsOnStakeholdersFormProvider/types';
import {
  checkAdditionalDescriptionEnRegex,
  checkAdditionalDescriptionThSpace,
  checkStakeholderGroupCompletionEn,
  checkStakeholderGroupCompletionTh,
  checkStkAnalysisAssetsValidEn,
  checkStkAnalysisAssetsValidTh,
  checkStkAnalysisIntroductionValidEn,
  checkStkAnalysisIntroductionValidTh,
  checkStkAnalysisRemarkEn,
  checkStkAnalysisRemarkTh,
} from '../StakeholderAnalysisValueChainOfBusiness/utils';
import { Role } from 'src/Enum/Report';
import {
  checkValidIntroductionEn,
  checkValidIntroductionTh,
  checkValidRemarkEn,
  checkValidRemarkTh,
} from '@Utils/validationIntroRemark';

const checkAllComplete = (
  bizCompleted?: boolean,
  stkAnalysisCompleted?: boolean
) => {
  switch (true) {
    case bizCompleted && stkAnalysisCompleted:
      return true;
    default:
      return false;
  }
};

const ImpactsOnStakeholdersCheckAll = () => {
  const { setValue, control } = useImpactsOnStakeholdersForm();
  const { isPublished } = useProgressBar();
  const [businessValueChain, stakeholderAnalysis, introduction, remark] =
    useWatch({
      name: [
        'businessValueChain',
        'stakeholderAnalysis',
        'introduction',
        'remark',
      ],
      control,
    });

  const { stakeholderGroups } =
    stakeholderAnalysis as StakeholderAnalysisFormValues;

  const stkAnalysisCompleted =
    stakeholderAnalysis?.completedSection as CompleteSectionFormValues;
  const bizCompleted =
    businessValueChain?.completedSection as CompleteSectionFormValues;

  const otherDescriptions = useWatch({ name: 'otherDescriptions' });

  useEffect(() => {
    const determineCompletion = (language: 'Th' | 'En') => {
      const isLanguageCompleted = checkAllComplete(
        bizCompleted?.[`isCompleted${language}`],
        stkAnalysisCompleted?.[`isCompleted${language}`]
      );

      if (!otherDescriptions?.length) {
        if (language === 'Th') {
          setValue('completedSection.dateTh', bizCompleted?.dateTh);
          setValue('completedSection.roleStampTh', bizCompleted?.roleStampTh);
        } else {
          setValue('completedSection.dateEn', bizCompleted?.dateEn);
          setValue('completedSection.roleStampEn', bizCompleted?.roleStampEn);
        }
        return isLanguageCompleted;
      }

      const validator =
        language === 'Th'
          ? validateThaiDescription
          : validateEnglishDescription;

      const isValid = isLanguageCompleted && validator(otherDescriptions);

      if (isValid) {
        if (language === 'Th') {
          setValue('completedSection.dateTh', bizCompleted?.dateTh);
          setValue('completedSection.roleStampTh', bizCompleted?.roleStampTh);
        } else {
          setValue('completedSection.dateEn', bizCompleted?.dateEn);
          setValue('completedSection.roleStampEn', bizCompleted?.roleStampEn);
        }
      }

      return isValid;
    };

    setValue('completedSection.isCompletedTh', determineCompletion('Th'));
    setValue('completedSection.isCompletedEn', determineCompletion('En'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bizCompleted, stkAnalysisCompleted, setValue, otherDescriptions]);

  const isOverviewCompleteTh =
    checkValidIntroductionTh(introduction) && checkValidRemarkTh(remark);
  const isOverviewCompleteEn =
    checkValidIntroductionEn(introduction) && checkValidRemarkEn(remark);

  const isBusinessValueCompleteTh =
    checkBizValueCompletionTh(businessValueChain) &&
    checkBizAssetsValidTh(businessValueChain) &&
    checkBizRemarkThValid(businessValueChain) &&
    checkBizIntroductionThValid(businessValueChain);

  const isBusinessValueCompleteEn =
    checkBizValueCompletionEn(businessValueChain) &&
    checkBizIntroductionEnValid(businessValueChain) &&
    checkBizAssetsValidEn(businessValueChain) &&
    checkBizRemarkEnValid(businessValueChain);

  const isStkGroupCompleteTh =
    checkStakeholderGroupCompletionTh(stakeholderGroups) &&
    checkStkAnalysisAssetsValidTh(stakeholderAnalysis) &&
    checkStkAnalysisRemarkTh(stakeholderAnalysis?.remark) &&
    checkStkAnalysisIntroductionValidTh(stakeholderAnalysis?.introduction) &&
    checkAdditionalDescriptionThSpace(
      stakeholderAnalysis?.additionalDescriptionTh
    );

  const isStkGroupCompleteEn =
    checkStakeholderGroupCompletionEn(stakeholderGroups) &&
    checkStkAnalysisRemarkEn(stakeholderAnalysis?.remark) &&
    checkStkAnalysisIntroductionValidEn(stakeholderAnalysis?.introduction) &&
    checkStkAnalysisAssetsValidEn(stakeholderAnalysis) &&
    checkAdditionalDescriptionEnRegex(
      stakeholderAnalysis?.additionalDescriptionEn
    );

  const isOtherDescriptionValidTh =
    otherDescriptions?.length >= 0
      ? validateThaiDescription(otherDescriptions)
      : true;

  const isOtherDescriptionValidEn =
    otherDescriptions?.length >= 0
      ? validateEnglishDescription(otherDescriptions)
      : true;

  const handleEachThComplete = (completed: boolean) => {
    setValue('businessValueChain.completedSection.isCompletedTh', completed);
    setValue('stakeholderAnalysis.completedSection.isCompletedTh', completed);
    setValue('completedSection.isCompletedTh', completed);
    if (!completed) {
      setValue('businessValueChain.completedSection.roleStampTh', Role.NONE);
      setValue('businessValueChain.completedSection.dateTh', undefined);
      setValue('stakeholderAnalysis.completedSection.roleStampTh', Role.NONE);
      setValue('stakeholderAnalysis.completedSection.dateTh', undefined);
    }
  };

  const handleEachEnComplete = (completed: boolean) => {
    setValue('businessValueChain.completedSection.isCompletedEn', completed);
    setValue('stakeholderAnalysis.completedSection.isCompletedEn', completed);
    setValue('completedSection.isCompletedEn', completed);
    if (!completed) {
      setValue('businessValueChain.completedSection.roleStampEn', Role.NONE);
      setValue('businessValueChain.completedSection.dateEn', undefined);
      setValue('stakeholderAnalysis.completedSection.roleStampEn', Role.NONE);
      setValue('stakeholderAnalysis.completedSection.dateEn', undefined);
    }
  };

  return (
    <Box bgcolor={'white'}>
      <Box p={2}>
        <Typography fontWeight={800}>
          กรณีต้องการยืนยันความถูกต้องทุกหัวข้อ
        </Typography>
      </Box>
      <Divider />
      <Flex gap={4} justifyContent={'center'} bgcolor={'grey.50'} py={1}>
        <Flex alignItems={'center'}>
          <Typography fontWeight={800} textAlign={'right'}>
            ส่วนการยืนยันการตรวจสอบข้อมูลครบถ้วนทุกหัวข้อ
          </Typography>
        </Flex>
        <Flex>
          <Flex flexDirection="column" alignItems={'center'}>
            <FormCheckbox
              name="completedSection.isCompletedTh"
              label="จัดเตรียมข้อมูลเรียบร้อย (ภาษาไทย)"
              inputProps={{
                disabled:
                  isPublished ||
                  !isOverviewCompleteTh ||
                  !isBusinessValueCompleteTh ||
                  !isStkGroupCompleteTh ||
                  !isOtherDescriptionValidTh,
              }}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                handleEachThComplete(event.target.checked);
              }}
            />
          </Flex>
          <Flex flexDirection="column" alignItems={'center'}>
            <FormCheckbox
              sx={{ ml: 2 }}
              name="completedSection.isCompletedEn"
              label="จัดเตรียมข้อมูลเรียบร้อย (ภาษาอังกฤษ)"
              inputProps={{
                disabled:
                  isPublished ||
                  !isOverviewCompleteEn ||
                  !isBusinessValueCompleteEn ||
                  !isStkGroupCompleteEn ||
                  !isOtherDescriptionValidEn,
              }}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                handleEachEnComplete(event.target.checked);
              }}
            />
          </Flex>
        </Flex>
      </Flex>
    </Box>
  );
};

export default ImpactsOnStakeholdersCheckAll;
